const vertex = `
#define HALF_PI 1.5707963267948966

precision highp float;
precision highp int;

attribute vec3 position;
attribute vec3 normal;
attribute vec2 uv;

// Instanced attributes
attribute vec3 random;
attribute vec3 offset;
attribute vec2 color;
attribute vec2 xy;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;

uniform float uTime;
uniform float uProgressPoolA;
uniform float uProgressPoolB;

varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vRandom;
varying vec2 vXY;
varying vec2 vColor;
varying float vDepth;
varying float vProgress;

float map(float value, float inMin, float inMax, float outMin, float outMax) {
  return outMin + (outMax - outMin) * (value - inMin) / (inMax - inMin);
}

float sineOut(float t) {
  return sin(t * HALF_PI);
}

void main() {
    vNormal = normalize(normalMatrix * normal);
    vUv = uv;
    vRandom = random;
    vColor = color;
    vXY = xy;

    // Split instances into two groups with
    // different progress value (0 or 1)
    vProgress = mix(uProgressPoolA, uProgressPoolB, step(0.5, random.z));

    float start = mix(-140., -110., vRandom.x);
    float end = mix(0., 200., vRandom.y);
    float z = min(mix(start, end, vProgress), 0.);

    vDepth = map(z, -110., 0., 0., 1.);

    vec3 pos = position;
    pos.x += offset.x;
    pos.y += offset.y;
    pos.z = z;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

const fragment = `
precision highp float;
precision highp int;

#define fullTexSize 2048.
#define colorTexSize 1024.
#define photoTexSize 256.
#define photosCount colorTexSize / photoTexSize
#define tilesX 48.
#define tilesY 26.

uniform float uTime;
uniform sampler2D tMap;
uniform sampler2D tOverlay;
uniform float uMaxAlpha;
uniform float uBrightnessMix;
uniform float uDissolve;
uniform float uAlpha;
uniform float uOverlay;

varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vRandom;
varying vec2 vXY;
varying vec2 vColor;
varying float vDepth;
varying float vProgress;

float map(float value, float inMin, float inMax, float outMin, float outMax) {
  return outMin + (outMax - outMin) * (value - inMin) / (inMax - inMin);
}

vec2 remapUV(vec2 uv, float textureSize, float tileSize, vec2 coord, float crop) {
    float zoom = tileSize / textureSize; 	
    vec2 offset = coord * tileSize;
    vec2 size = vec2(textureSize, textureSize);
    vec2 mappedUV = vec2(offset.x / size.x, offset.y / size.y);
    // subtract a little bit to zoom closer and avoid sampeling edges
    return fract(uv) * (zoom - crop) + mappedUV;
}

vec2 overlayUV(vec2 uv, vec2 size, vec2 tileSize, vec2 coord) {
    vec2 zoom = tileSize / size; 	
    vec2 offset = coord * tileSize;
    vec2 mappedUV = vec2(offset.x / size.x, offset.y / size.y);
    return fract(uv) * zoom + mappedUV;
}

float luma(vec3 color) {
  return dot(color, vec3(0.299, 0.587, 0.114));
}

void main() {
    // Get the correct color section
    vec2 colorCoord = vec2(vColor.x, vColor.y);

    // Get random coords for photo
    // round = add 0.5 and floor
    vec2 randomCoord = vec2(
      floor(vRandom.x * photosCount),
      floor(vRandom.y * photosCount)
    );

    // Get color section based on attributes
    vec2 mappedUV = remapUV(vUv, fullTexSize, colorTexSize, colorCoord, 0.0);

    // Get random photo from inside color section
    vec2 uv = remapUV(vUv, fullTexSize, photoTexSize, randomCoord, 0.01);

    // add color block size offset
    uv += colorCoord * (colorTexSize / fullTexSize);
    
    vec3 tex = texture2D(tMap, uv).rgb;

    // control birghtness
    float brightness = luma(tex);
    tex = mix(vec3(brightness) * 0.2, tex, uBrightnessMix);

    float alpha = map(vDepth, 0.7, 0.9, 0., 1.); // How far back do we want to see tiles
    alpha = mix(alpha, vProgress, vProgress - 0.2); // Mix with progress

    // THIS IS HARD CODED TO MATCH TILES
    vec2 oUV = overlayUV(vUv, vec2(tilesX, tilesY), vec2(1., 1.), vXY);
    vec3 overlay = texture2D(tOverlay, oUV).rgb;

    // GET UNIFORM THAT CONTROL OVERLAY
    gl_FragColor.rgb = mix(tex, overlay, uOverlay);

    float depth = min(alpha, uMaxAlpha);
    float start = mix(1., 1.5, vRandom.x);
    float end = mix(0.0, -1.5, vRandom.z);
    float dissolve = mix(start, end, uDissolve);
    gl_FragColor.a = depth * dissolve * uAlpha; 
}
`;

export default {vertex, fragment};
